import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MapPinIcon, ThumbsUpIcon, UsersIcon } from 'lucide-react';
import { profileCoordinates, publicReportStatusLabels, publicReports, reportCategories, type PublicReport, type PublicReportCategory } from '../../data/demandes';
import { token } from '../../utils/theme';
type Filter = 'tous' | PublicReportCategory;
const filters: Filter[] = ['tous', 'danger', 'voirie', 'eclairage', 'arbre', 'eau'];

/** L'orange de Beaconsfield porte du texte blanc, jamais du texte foncé. */
const statusStyles: Record<PublicReport['status'], string> = {
  nouveau: 'bg-ville-lime text-white',
  'en-traitement': 'bg-ville-indigo-soft text-ville-indigo',
  resolue: 'bg-slate-100 text-slate-500'
};
function pinIcon(report: PublicReport, active: boolean): L.DivIcon {
  const color = reportCategories[report.category].color;
  const size = active ? 34 : 24;
  return L.divIcon({
    className: 'atna-pin',
    html: `<span style="background:${color};width:${size}px;height:${size}px" class="block rounded-full border-[3px] border-white shadow-md shadow-ville-navy/30 ${active ? 'ring-2 ring-ville-navy/20' : ''}"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}

/** Pin « Signaler » posé à l'endroit cliqué sur une zone vide de la carte. */
const draftPinIcon = L.divIcon({
  className: 'atna-draft-pin',
  html: `<div class="flex flex-col items-center">
    <span class="whitespace-nowrap rounded-full bg-ville-navy px-3.5 py-1.5 text-xs font-bold text-white shadow-lg shadow-ville-navy/30 cursor-pointer">Signaler</span>
    <span class="mt-1 block h-5 w-5 rotate-45 rounded-full rounded-br-none border-[3px] border-white bg-ville-navy shadow-md"></span>
  </div>`,
  iconSize: [96, 58],
  iconAnchor: [48, 56]
});

/** Adresse lisible pour un point de la carte (Nominatim, sans clé), avec repli sur les coordonnées. */
async function reverseGeocode(lat: number, lng: number): Promise<string> {
  const fallback = `Près de ${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=fr`);
    if (!response.ok) return fallback;
    const data: {
      address?: {
        house_number?: string;
        road?: string;
        pedestrian?: string;
        park?: string;
      };
    } = await response.json();
    const road = data.address?.road ?? data.address?.pedestrian ?? data.address?.park;
    if (!road) return fallback;
    return data.address?.house_number ? `${data.address.house_number}, ${road}` : road;
  } catch {
    return fallback;
  }
}
interface PublicReportsMapProps {
  /** Ouvre ATNA avec le lieu cliqué déjà choisi. */
  onReportAt: (address: string, position: [number, number]) => void;
  /** Signalements envoyés pendant la session, ajoutés en tête de la carte. */
  extraReports?: PublicReport[];
}
export function PublicReportsMap({
  onReportAt,
  extraReports = []
}: PublicReportsMapProps) {
  const [filter, setFilter] = useState<Filter>('tous');
  const draftRef = useRef<L.Marker | null>(null);
  const onReportAtRef = useRef(onReportAt);
  onReportAtRef.current = onReportAt;
  const [selectedId, setSelectedId] = useState<string>(publicReports[0].id);
  const [confirmedIds, setConfirmedIds] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const allReports = useMemo(() => [...extraReports, ...publicReports], [extraReports]);
  const visible = useMemo(() => filter === 'tous' ? allReports : allReports.filter(report => report.category === filter), [filter, allReports]);
  const selected = visible.find(report => report.id === selectedId) ?? visible[0];
  const selectedIndex = selected ? visible.indexOf(selected) : -1;
  const newCount = allReports.filter(report => report.status === 'nouveau').length;

  // Un signalement vient d'être envoyé : le pin « Signaler » laisse place au vrai point, sélectionné.
  const latestExtraId = extraReports[0]?.id;
  useEffect(() => {
    if (!latestExtraId) return;
    draftRef.current?.remove();
    draftRef.current = null;
    setFilter('tous');
    setSelectedId(latestExtraId);
  }, [latestExtraId]);
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: profileCoordinates,
      zoom: 14,
      zoomControl: false,
      scrollWheelZoom: false,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    // Tuiles OpenStreetMap : gratuites et sans clé d'API.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    L.circleMarker(profileCoordinates, {
      radius: 7,
      color: '#ffffff',
      weight: 3,
      fillColor: token('--ville-navy', '#16265c'),
      fillOpacity: 1
    }).bindTooltip('Votre adresse').addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Leaflet ne voit pas les changements de taille du conteneur : on le prévient.
    const resizeObserver = new ResizeObserver(() => map.invalidateSize({
      pan: false
    }));
    resizeObserver.observe(containerRef.current);

    // Clic sur une zone sans signalement : on pose un pin « Signaler » à cet endroit.
    map.on('click', (event: L.LeafletMouseEvent) => {
      const {
        lat,
        lng
      } = event.latlng;
      draftRef.current?.remove();
      let address: string | null = null;
      const lookup = reverseGeocode(lat, lng).then(result => {
        address = result;
        return result;
      });
      const open = async () => onReportAtRef.current(address ?? (await lookup), [lat, lng]);
      draftRef.current = L.marker([lat, lng], {
        icon: draftPinIcon,
        keyboard: true,
        title: 'Signaler un problème à cet endroit',
        alt: 'Signaler un problème à cet endroit',
        zIndexOffset: 1000
      }).on('click', open).on('keypress', open).addTo(map);
    });
    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    visible.forEach(report => {
      L.marker([report.lat, report.lng], {
        icon: pinIcon(report, report.id === selected?.id),
        keyboard: true,
        title: `${reportCategories[report.category].label} — ${report.title}`,
        alt: `${reportCategories[report.category].label} — ${report.title}`,
        zIndexOffset: report.id === selected?.id ? 500 : 0
      }).on('click', () => setSelectedId(report.id)).on('keypress', () => setSelectedId(report.id)).addTo(layer);
    });
  }, [visible, selected?.id]);
  useEffect(() => {
    if (!mapRef.current || !selected) return;
    mapRef.current.panTo([selected.lat, selected.lng], {
      animate: true,
      duration: 0.3
    });
  }, [selected?.id]);
  const step = (direction: -1 | 1) => {
    if (visible.length === 0) return;
    const next = (selectedIndex + direction + visible.length) % visible.length;
    setSelectedId(visible[next].id);
  };
  const toggleConfirm = (id: string) => {
    setConfirmedIds(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  };
  return <section aria-labelledby="carte-publique-titre"
  /* `isolate` garde les couches Leaflet (contrôles, fiche flottante) dans leur propre
     contexte d'empilement, sinon elles passent au-dessus du dialogue ATNA. */ className="relative isolate z-0 flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white" data-id="element-1274" data-mp="1u2mofv5567c6">
      <div className="px-5 pb-4 pt-5 sm:px-6" data-id="element-1275" data-mp="1ebcpxkbrg416">
        <div data-id="element-1276" data-mp="1e7yms9v9vqso">
          <h2 id="carte-publique-titre" className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1277" data-mp="1a8y2so126i43">
            Signalements autour de vous
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-1278" data-mp="159v9u1ycs2ch">
            {allReports.length} signalements actifs · {newCount} nouveaux aujourd’hui
          </p>
        </div>

        <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1279" data-mp="1xx8zrg3mvxn3">
          {filters.map(option => {
          const active = filter === option;
          const label = option === 'tous' ? 'Tous' : reportCategories[option].label;
          return <li key={option} data-id="element-1280" data-mp="1o64jmd2826qo">
                <button type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-navy bg-ville-navy text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-1281" data-mp="1j9wbhjmfzdt0">
                  {option !== 'tous' && <span className="h-2 w-2 rounded-full" style={{
                backgroundColor: reportCategories[option].color
              }} aria-hidden="true" data-id="element-1282" data-mp="3282c8teludsl" />}
                  {label}
                </button>
              </li>;
        })}
        </ul>
      </div>

      {/* La carte occupe toute la hauteur restante : quand la liste « Mes demandes » à côté
          s'allonge, la carte grandit avec le conteneur au lieu de laisser un vide. */}
      <div className="relative flex min-h-[300px] flex-1 flex-col sm:min-h-[380px]" data-id="element-1283" data-mp="3pyoiqg8ynq0h">
        <div ref={containerRef} role="application" aria-label="Carte des signalements publics à Beaconsfield" className="min-h-[300px] w-full flex-1 bg-ville-mist sm:min-h-[380px]" data-id="element-1284" data-mp="2vpzmh5ulnmjq" />

        {selected ? <motion.div key={selected.id} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-ville-navy/10 sm:inset-x-4" data-id="element-1285" data-mp="35lktxjiftf0v">
            <div className="flex items-start gap-3" data-id="element-1286" data-mp="3obsr7kzghb95">
              <span className="mt-1 h-3 w-3 shrink-0 rounded-full" style={{
            backgroundColor: reportCategories[selected.category].color
          }} aria-hidden="true" data-id="element-1287" data-mp="3tbxatjvnum3l" />
              <div className="min-w-0 flex-1" data-id="element-1288" data-mp="1yzh7aqu91m2f">
                <div className="flex flex-wrap items-center gap-2" data-id="element-1289" data-mp="1rf6adtnjj8er">
                  <h3 className="text-[15px] font-extrabold leading-tight text-ville-navy" data-id="element-1290" data-mp="1rhj1awba0t3y">{selected.title}</h3>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${statusStyles[selected.status]}`} data-id="element-1291" data-mp="1mmv8wmd5jek3">
                    {publicReportStatusLabels[selected.status]}
                  </span>
                </div>
                <p className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-1292" data-mp="2aetzedl69ght">
                  <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1293" data-mp="25fh557rz1757" />
                  <span className="truncate" data-id="element-1294" data-mp="20g4gqilgbs0i">{selected.address}</span>
                </p>
                <p className="mt-0.5 text-[13px] text-slate-400" data-id="element-1295" data-mp="20hxok17swia3">
                  À {selected.distance} · {selected.reportedAgo}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1" data-id="element-1296" data-mp="p2irkxfldqez">
                <button type="button" onClick={() => step(-1)} aria-label="Signalement précédent" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1297" data-mp="k1lc4slyvah6">
                  <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1298" data-mp="337nnpj1a2hkj" />
                </button>
                <span className="text-[11px] font-semibold tabular-nums text-slate-400" data-id="element-1299" data-mp="2oiriruuipko5">
                  {selectedIndex + 1}/{visible.length}
                </span>
                <button type="button" onClick={() => step(1)} aria-label="Signalement suivant" className="grid h-8 w-8 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1300" data-mp="2fjeah5i4r250">
                  <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1301" data-mp="1qxhnvorcdfyb" />
                </button>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3" data-id="element-1302" data-mp="1myh86f45utfj">
              <p className="flex items-center gap-1.5 text-[13px] text-slate-500" data-id="element-1303" data-mp="20xql95x2l7fa">
                <UsersIcon className="h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-1304" data-mp="1vvrkm3spsom1" />
                {selected.confirmations + (confirmedIds.includes(selected.id) ? 1 : 0)} personnes le confirment
              </p>
              <button type="button" onClick={() => toggleConfirm(selected.id)} aria-pressed={confirmedIds.includes(selected.id)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 ${confirmedIds.includes(selected.id) ? 'bg-ville-indigo-soft text-ville-indigo' : 'bg-ville-navy text-white hover:bg-ville-navy/90'}`} data-id="element-1305" data-mp="25va3si0sw6d1">
                <ThumbsUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-1306" data-mp="vomh9dm0xc3w" />
                {confirmedIds.includes(selected.id) ? 'Confirmé' : 'Je confirme'}
              </button>
            </div>
          </motion.div> : <div className="absolute inset-x-3 bottom-3 z-[900] rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 shadow-xl shadow-ville-navy/10" data-id="element-1307" data-mp="31s8vf2he36s">
            Aucun signalement dans cette catégorie pour le moment.
          </div>}
      </div>
    </section>;
}